import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import merge from "lodash/merge";
import {
  HTTP_HEADER_ACCEPT,
  HTTP_HEADER_CONTENT_TYPE,
  HTTP_MEDIA_TYPE_PROTOBUF,
  REQUEST_CONFIG_NAMESPACE,
} from "./constants";
import { DecoratedHttpClient } from "./decorated-http-client";
import {
  EncodableProtobufMessage,
  HttpClient,
  ProtobufAxiosRequestConfig,
} from "./types";
import { base64urlEncodeWithoutPadding } from "./utils";

/**
 * This HTTP client implements logic to serialize outgoing request payloads
 * from pain JavaScript objects to protobuf messages. It also implements logic
 * to de-serialize incoming response payloads from protobuf messages to plain
 * JavaScript objects.
 *
 * It relies on the [`protobufjs`](https://github.com/protobufjs/protobuf.js)
 * library for its underlying implementation, and assumes that it is used via
 * [statically generated code](https://github.com/protobufjs/protobuf.js#using-generated-static-code).
 *
 * A special configuration property needs to be added to the Axios request config
 * object, which is passed in while calling any of this client's APIs. This
 * configuration is name-spaced under the `ftdrHttpClient` property on the Axios
 * request config object. The `ftdrHttpClient` key must be set to an object with
 * configuration properties specific to the HTTP clients implemented in this
 * library. For protobuf calls the properties of the [`ProtobufConfig`](../interfaces/ProtobufConfig.md)
 * interface to be set.
 *
 * The config object needs to be set with the correct proto message model class
 * generated by `protobufjs` that is to be used to serialize/de-serialize the
 * request/response/errorResponse.
 *
 * @example
 *
 * ```typescript
 * import { ProtobufHttpClient } from "@ftdr/http-utils";
 *
 * // `somepb` here represents the proto message model code generated by `protobufjs`
 * import { somepb } from "@ftdr/protos";
 *
 * const protobufHttpClient = new ProtobufHttpClient();
 *
 * // create an instance of the proto message js class and fill it with request data
 * // This request object will be encoded to protobuf message using `requestProtobufModel`
 * // in the below config object.
 * const requestMessage: somepb.SomeRequestMessage = somepb.SomeRequestMessage.create();
 * requestMessage.someField = "some value";
 * requestMessage.someOtherField = "some other value";
 *
 * try {
 *   const response = await protobufHttpClient.post(
 *     "https://api.example.com/entities",
 *     requestMessage,
 *     // pass the appropriate protobuf model classes generated by `protobufjs`
 *     // for encoding/decoding request/response/errorResponse data.
 *     // Note that they are all optional. If not passed, that specific encoding/decoding
 *     // is not attempted.
 *     {
 *       ftdrHttpClient: {
 *         requestProtobufModel: somepb.SomeRequestMessage,
 *         responseProtobufModel: somepb.SomeResponseMessage,
 *         errorResponseProtobufModel: somepb.SomeResponseErrorMessage,
 *       },
 *     }
 *   );
 *
 *   // `response.data` contains the result of decoding using `responseProtobufModel` and ready to use.
 *   console.log(response.data);
 * } catch (error) {
 *   if (error.response?.status === 400) {
 *     // `error.response?.data` contains the result of decoding using the same `errorResponseProtobufModel`
 *     // and is ready to use further.
 *     const errorCode = error.response?.data.errors[0].code;
 *     console.error("API Call failed with error code: ", errorCode);
 *   } else {
 *     console.error("Unexpected API call failure!", error.message);
 *   }
 * }
 * ```
 *
 * ### A Note on Encoding
 *
 * The `getWithRequestDataAsUrlParam` and `deleteWithRequestDataAsUrlParam` methods
 * of `ProtobufHttpClient` send the protobuf request data as an encoded string
 * value of the `proto_body` (configurable) URL query parameter.
 *
 * They use the base64url (without padding) encoding scheme for this encoding.
 * This is as per the [RFC-4648](https://tools.ietf.org/html/rfc4648) standard.
 *
 * You can read more about this in the RFC document:
 *
 * - [Section 5 - Base 64 Encoding with URL and Filename Safe Alphabet](https://tools.ietf.org/html/rfc4648#section-5)
 * - [Section 3.2 - Padding of Encoded Data](https://tools.ietf.org/html/rfc4648#section-3.2)
 *
 * Essentially, this encoding scheme eliminates `+` or `/` characters from the
 * encoded string, thus making it safe for including in a URL.
 *
 * > **NOTE:** For correct processing of the protobuf request data encoded in this
 * > way, the backend service handling this request must use a compatible Base 64
 * > decoding scheme.
 */
export class ProtobufHttpClient<
  C extends AxiosRequestConfig = AxiosRequestConfig
> extends DecoratedHttpClient<ProtobufAxiosRequestConfig & C> {
  defaults = {
    responseType: "arraybuffer",
    headers: {
      common: {
        [HTTP_HEADER_ACCEPT]: HTTP_MEDIA_TYPE_PROTOBUF,
      },
      get: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_PROTOBUF,
      },
      delete: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_PROTOBUF,
      },
      post: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_PROTOBUF,
      },
      put: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_PROTOBUF,
      },
      patch: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_PROTOBUF,
      },
    },
  } as ProtobufAxiosRequestConfig;

  /**
   * Construct a new `ProtobufHttpClient`
   *
   * @param httpClient An optional HttpClient instance. Use this to compose
   *                   functionality of multiple HttpClient instances into a
   *                   single instance.
   */
  constructor(httpClient?: HttpClient) {
    super(httpClient);
    this.mergeDefaults();
  }

  /**
   * Sends an HTTP request as per the `config` passed. It additionally sets up
   * common, request pre-processing and response post-processing logic to handle
   * protobuf encoding/decoding.
   *
   * @param config AxiosRequestConfig object. If passed, it is merged with the
   * `defaults` on the HttpClient instance. If not passed, then simply uses the
   * `defaults.`
   */
  request = <T = any>(
    config: ProtobufAxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    const configWithEncodedRequestData = getConfigWithEncodedRequestData(
      config
    );

    const requestConfig = merge(config, configWithEncodedRequestData);

    return this.httpClient
      .request(requestConfig)
      .then(decodeResponseData)
      .catch(decodeResponseError);
  };

  private requestWithRequestDataAsUrlParam = <D = any, T = any>(
    ownConfig: AxiosRequestConfig,
    data?: D,
    config: ProtobufAxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> => {
    const configWithParams = getConfigWithEncodedRequestDataAsUrlParams(
      data,
      config
    );

    const requestConfig = merge(config, configWithParams, ownConfig);

    return this.httpClient
      .request(requestConfig)
      .then(decodeResponseData)
      .catch(decodeResponseError);
  };

  /**
   * Sends an HTTP GET request as per the `config` passed, and request data is
   * sent as a URL param.
   *
   * @param url The URL to call.
   * @param data Request data object, it will be sent as a URL param.
   * @param config AxiosRequestConfig object.
   */
  getWithRequestDataAsUrlParam = <D = any, T = any>(
    url: string,
    data?: D,
    config: ProtobufAxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> => {
    const ownConfig: AxiosRequestConfig = { url, method: "get" };

    return this.requestWithRequestDataAsUrlParam(ownConfig, data, config);
  };

  /**
   * Sends an HTTP DELETE request as per the `config` passed, and request data is
   * sent as a URL param.
   *
   * @param url The URL to call.
   * @param data Request data object, it will be sent as a URL param.
   * @param config AxiosRequestConfig object.
   */
  deleteWithRequestDataAsUrlParam = <D = any, T = any>(
    url: string,
    data?: D,
    config: ProtobufAxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> => {
    const ownConfig: AxiosRequestConfig = { url, method: "delete" };

    return this.requestWithRequestDataAsUrlParam(ownConfig, data, config);
  };
}

const getConfigWithEncodedRequestData = <M>(
  config: ProtobufAxiosRequestConfig
): AxiosRequestConfig | undefined => {
  const requestProtobufModel =
    config[REQUEST_CONFIG_NAMESPACE]?.requestProtobufModel;

  const requestData = config.data;

  if (requestProtobufModel && requestData) {
    const { buffer, byteOffset, byteLength } = encodeRequestData<M>(
      requestData,
      requestProtobufModel
    );

    /**
     * We should ideally be able to simply return `buffer`.
     * However `protobufjs` does not seem to like that.
     * So we perform the below no-op on the `buffer` and return the result.
     * This, for some reason, seems to do the trick. ¯\_(ツ)_/¯
     */
    const encodedRequestData = buffer.slice(
      byteOffset,
      byteOffset + byteLength
    );

    return {
      data: encodedRequestData,
    };
  }

  return undefined;
};

const getConfigWithEncodedRequestDataAsUrlParams = <M>(
  requestData: M,
  config: ProtobufAxiosRequestConfig
): AxiosRequestConfig | undefined => {
  const requestProtobufModel =
    config[REQUEST_CONFIG_NAMESPACE]?.requestProtobufModel;
  const requestDataUrlParamName =
    config[REQUEST_CONFIG_NAMESPACE]?.requestDataUrlParamName ?? "proto_body";

  if (requestProtobufModel && requestData) {
    const encodedRequestData = encodeRequestData<M>(
      requestData,
      requestProtobufModel
    );
    const base64EncodedData = base64urlEncodeWithoutPadding(encodedRequestData);
    return {
      params: {
        [requestDataUrlParamName]: base64EncodedData,
      },
    };
  }

  return undefined;
};

const encodeRequestData = <M>(
  requestData: M,
  requestProtobufModel: EncodableProtobufMessage<M>
) => {
  return requestProtobufModel.encode(requestData).finish();
};

const decodeResponseData = (
  response: AxiosResponse<any>
): AxiosResponse<any> => {
  const responseProtobufModel = (response?.config as ProtobufAxiosRequestConfig)?.[
    REQUEST_CONFIG_NAMESPACE
  ]?.responseProtobufModel;

  if (response.data && responseProtobufModel) {
    response.data = responseProtobufModel.decode(new Uint8Array(response.data));
  }

  return response;
};

const decodeResponseError = (error: AxiosError<any>) => {
  const errorResponseProtobufModel = (error?.config as ProtobufAxiosRequestConfig)?.[
    REQUEST_CONFIG_NAMESPACE
  ]?.errorResponseProtobufModel;

  if (error.response?.data && errorResponseProtobufModel) {
    error.response.data = errorResponseProtobufModel.decode(
      new Uint8Array(error.response.data)
    );
  }

  throw error;
};
